/**
 * Catálogo de equipamentos (`/catalogo`). Compartilhado — qualquer pessoa logada
 * pode editar. Lista, busca, filtro de ativos, criação (com a primeira versão de
 * preço), ativar/desativar, exclusão e reordenação manual (sem drag-and-drop).
 */
import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError } from '@/lib/api';
import { fmtDate } from '@/lib/format';
import type { CreateProductInput } from '@/lib/types';
import {
  hojeISO,
  parseValorMonetario,
  percentTextoParaFracao,
  useCriarProduto,
  useAtualizarProduto,
  useExcluirProduto,
  useProdutos,
  useReordenarProdutos,
  type ProdutoCatalogo,
} from '@/hooks/useCatalogo';
import { useToast } from '@/hooks/useToast';
import { PageHeader } from '@/components/PageHeader';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { EmptyState } from '@/components/EmptyState';
import { Badge } from '@/components/Badge';
import { Money } from '@/components/Money';
import { Percent } from '@/components/Percent';
import { Button } from '@/components/Button';
import { Field } from '@/components/Field';
import { Modal } from '@/components/Modal';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import './Catalogo.css';

type FiltroAtivo = 'all' | '1' | '0';

/** Data curta dia/mês (ex.: "01/03") — só para o aviso discreto na tabela. */
function fmtDiaMes(iso: string): string {
  const d = new Date(`${iso}T12:00:00`);
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

export function Catalogo() {
  const navigate = useNavigate();
  const toast = useToast();

  const [busca, setBusca] = useState('');
  const [buscaDebounced, setBuscaDebounced] = useState('');
  const [filtroAtivo, setFiltroAtivo] = useState<FiltroAtivo>('all');
  const [modalNovoAberto, setModalNovoAberto] = useState(false);
  const [paraExcluir, setParaExcluir] = useState<ProdutoCatalogo | null>(null);
  const [erroExclusao, setErroExclusao] = useState<string | null>(null);

  useEffect(() => {
    const t = window.setTimeout(() => setBuscaDebounced(busca.trim()), 300);
    return () => window.clearTimeout(t);
  }, [busca]);

  const filtrosLimpos = buscaDebounced === '' && filtroAtivo === 'all';
  const produtosQuery = useProdutos({ q: buscaDebounced || undefined, active: filtroAtivo });
  const produtos = produtosQuery.data ?? [];

  const atualizar = useAtualizarProduto();
  const excluir = useExcluirProduto();
  const reordenar = useReordenarProdutos();

  function mover(indice: number, direcao: -1 | 1) {
    if (!filtrosLimpos || reordenar.isPending) return;
    const alvo = indice + direcao;
    if (alvo < 0 || alvo >= produtos.length) return;
    const nova = produtos.slice();
    [nova[indice], nova[alvo]] = [nova[alvo], nova[indice]];
    reordenar.mutate(nova.map((p) => p.id));
  }

  function alternarAtivo(produto: ProdutoCatalogo) {
    atualizar.mutate(
      { id: produto.id, input: { active: !produto.active } },
      {
        onSuccess: () => toast.success(produto.active ? `${produto.name} desativado.` : `${produto.name} ativado.`),
        onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Não foi possível salvar agora.'),
      }
    );
  }

  function confirmarExclusao() {
    if (!paraExcluir) return;
    setErroExclusao(null);
    excluir.mutate(paraExcluir.id, {
      onSuccess: () => {
        toast.success(`${paraExcluir.name} excluído.`);
        setParaExcluir(null);
      },
      onError: (err) => {
        setErroExclusao(err instanceof ApiError ? err.message : 'Não foi possível excluir agora.');
      },
    });
  }

  const colunas = useMemo<DataTableColumn<ProdutoCatalogo>[]>(
    () => [
      {
        key: 'posicao',
        header: 'Ordem',
        className: 'cat-reorder-cell',
        render: (row) => {
          const indice = produtos.findIndex((p) => p.id === row.id);
          return (
            <div className="cat-reorder">
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                aria-label={`Mover ${row.name} para cima`}
                disabled={!filtrosLimpos || indice <= 0 || reordenar.isPending}
                onClick={(e) => {
                  e.stopPropagation();
                  mover(indice, -1);
                }}
              >
                ▲
              </button>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                aria-label={`Mover ${row.name} para baixo`}
                disabled={!filtrosLimpos || indice < 0 || indice >= produtos.length - 1 || reordenar.isPending}
                onClick={(e) => {
                  e.stopPropagation();
                  mover(indice, 1);
                }}
              >
                ▼
              </button>
            </div>
          );
        },
      },
      {
        key: 'nome',
        header: 'Equipamento',
        render: (row) => (
          <div className="cat-name">
            <span className="cat-name__title">{row.name}</span>
            {row.details ? <span className="cat-name__details muted">{row.details}</span> : null}
          </div>
        ),
      },
      {
        key: 'acessorios',
        header: 'Acessórios',
        numeric: true,
        render: (row) => row.accessories.length,
      },
      {
        key: 'preco',
        header: 'Preço vigente',
        numeric: true,
        render: (row) =>
          row.currentPrice ? (
            <div className="cat-price">
              <Money value={row.currentPrice.unitValue} />
              {row.nextPrice ? (
                <span
                  className="cat-price__hint faint"
                  title={`Reajuste agendado: R$ ${row.nextPrice.unitValue.toLocaleString('pt-BR', {
                    minimumFractionDigits: 2,
                  })} a partir de ${fmtDate(row.nextPrice.validFrom)}.`}
                >
                  reajuste agendado
                </span>
              ) : null}
            </div>
          ) : (
            <div className="cat-price">
              <Badge variant="attention">sem preço vigente</Badge>
              {row.nextPrice ? (
                <span
                  className="cat-price__hint faint"
                  title="Sem preço vigente, este equipamento não aparece em Nova Proposta até essa data."
                >
                  entra em vigor em {fmtDiaMes(row.nextPrice.validFrom)}
                </span>
              ) : null}
            </div>
          ),
      },
      {
        key: 'ipi',
        header: 'IPI',
        numeric: true,
        render: (row) => (row.currentPrice ? <Percent value={row.currentPrice.ipi} /> : <span className="faint">—</span>),
      },
      {
        key: 'versoes',
        header: 'Versões',
        numeric: true,
        render: (row) => row.priceVersionCount,
      },
      {
        key: 'status',
        header: 'Status',
        render: (row) => <Badge variant={row.active ? 'positive' : 'neutral'}>{row.active ? 'Ativo' : 'Inativo'}</Badge>,
      },
      {
        key: 'acoes',
        header: 'Ações',
        className: 'cat-actions-cell',
        render: (row) => (
          <div className="row row-wrap cat-actions">
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={(e) => {
                e.stopPropagation();
                alternarAtivo(row);
              }}
              disabled={atualizar.isPending}
            >
              {row.active ? 'Desativar' : 'Ativar'}
            </button>
            <button
              type="button"
              className="btn btn-danger btn-sm"
              onClick={(e) => {
                e.stopPropagation();
                setErroExclusao(null);
                setParaExcluir(row);
              }}
            >
              Excluir
            </button>
          </div>
        ),
      },
    ],
    [produtos, filtrosLimpos, reordenar.isPending, atualizar.isPending]
  );

  return (
    <>
      <PageHeader
        title="Catálogo"
        meta="Os equipamentos e preços que alimentam a Nova Proposta. Qualquer pessoa do time pode manter esta tabela."
        actions={<Button onClick={() => setModalNovoAberto(true)}>Novo equipamento</Button>}
      />

      <div className="toolbar">
        <Field
          label="Buscar"
          wrapperClassName="cat-search"
          placeholder="Nome, detalhes ou identificador"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
        />
        <Field
          as="select"
          label="Status"
          wrapperClassName="cat-filter-status"
          value={filtroAtivo}
          onChange={(e) => setFiltroAtivo(e.target.value as FiltroAtivo)}
        >
          <option value="all">Todos</option>
          <option value="1">Ativos</option>
          <option value="0">Inativos</option>
        </Field>
        <div className="toolbar-spacer" />
      </div>

      {!filtrosLimpos ? (
        <p className="muted cat-reorder-hint">Limpe a busca e o filtro de status para reordenar os equipamentos.</p>
      ) : null}

      <DataTable
        columns={colunas}
        rows={produtos}
        rowKey={(row) => row.id}
        loading={produtosQuery.isLoading}
        onRowClick={(row) => navigate(`/catalogo/${row.id}`)}
        empty={
          filtrosLimpos ? (
            <EmptyState
              title="Nenhum equipamento cadastrado ainda"
              description="Cadastre o primeiro para começar a montar propostas."
              action={<Button onClick={() => setModalNovoAberto(true)}>Novo equipamento</Button>}
            />
          ) : (
            <EmptyState
              title="Nenhum equipamento encontrado"
              description="Tente outro termo de busca ou outro filtro de status."
              action={
                <Button
                  variant="ghost"
                  onClick={() => {
                    setBusca('');
                    setFiltroAtivo('all');
                  }}
                >
                  Limpar filtros
                </Button>
              }
            />
          )
        }
      />

      <ModalNovoEquipamento open={modalNovoAberto} onClose={() => setModalNovoAberto(false)} />

      <ConfirmDialog
        open={paraExcluir !== null}
        title="Excluir equipamento"
        tone="danger"
        confirmLabel="Excluir"
        loading={excluir.isPending}
        onCancel={() => {
          setParaExcluir(null);
          setErroExclusao(null);
        }}
        onConfirm={confirmarExclusao}
        message={
          <div className="stack-sm">
            <p>
              Excluir <strong>{paraExcluir?.name}</strong>? Esta ação não pode ser desfeita.
            </p>
            {erroExclusao ? (
              <p className="form-alert form-alert-error" role="alert">
                {erroExclusao}
              </p>
            ) : null}
          </div>
        }
      />
    </>
  );
}

// -------------------------------------------------------- modal · novo equipamento --

function ModalNovoEquipamento({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const toast = useToast();
  const criar = useCriarProduto();

  const [nome, setNome] = useState('');
  const [detalhes, setDetalhes] = useState('');
  const [acessorios, setAcessorios] = useState('');
  const [valor, setValor] = useState('');
  const [ipi, setIpi] = useState('0,00');
  const [validade, setValidade] = useState(hojeISO());
  const [nota, setNota] = useState('');
  const [erroGeral, setErroGeral] = useState<string | null>(null);
  const [erros, setErros] = useState<Record<string, string>>({});

  // Memoizado: passado como `onClose` para `<Modal>`, cujo efeito de foco depende dessa
  // referência — recriá-la a cada tecla (este componente re-renderiza a cada tecla, por
  // causa dos estados do formulário) fazia o foco pular do campo de volta para o modal
  // a cada caractere digitado.
  const fecharEReiniciar = useCallback(() => {
    setNome('');
    setDetalhes('');
    setAcessorios('');
    setValor('');
    setIpi('0,00');
    setValidade(hojeISO());
    setNota('');
    setErroGeral(null);
    setErros({});
    onClose();
  }, [onClose]);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setErroGeral(null);
    setErros({});

    const input: CreateProductInput = {
      name: nome,
      details: detalhes || undefined,
      accessories: acessorios || undefined,
      price: {
        unitValue: parseValorMonetario(valor),
        ipi: percentTextoParaFracao(ipi),
        validFrom: validade || undefined,
        note: nota || undefined,
      },
    };

    try {
      const criado = await criar.mutateAsync(input);
      toast.success(`${criado.name} cadastrado no catálogo.`);
      fecharEReiniciar();
      navigate(`/catalogo/${criado.id}`);
    } catch (err) {
      if (err instanceof ApiError) {
        setErroGeral(err.message);
        setErros(err.fields ?? {});
      } else {
        setErroGeral('Não foi possível cadastrar agora. Tente de novo.');
      }
    }
  }

  return (
    <Modal
      open={open}
      title="Novo equipamento"
      onClose={fecharEReiniciar}
      footer={
        <>
          <Button variant="ghost" onClick={fecharEReiniciar} disabled={criar.isPending}>
            Cancelar
          </Button>
          <Button type="submit" form="form-novo-equipamento" disabled={criar.isPending}>
            {criar.isPending ? 'Salvando…' : 'Cadastrar equipamento'}
          </Button>
        </>
      }
    >
      <form id="form-novo-equipamento" className="stack" onSubmit={handleSubmit} noValidate>
        {erroGeral ? (
          <p className="form-alert form-alert-error" role="alert">
            {erroGeral}
          </p>
        ) : null}
        <Field label="Nome" required value={nome} onChange={(e) => setNome(e.target.value)} error={erros.name} />
        <Field
          as="textarea"
          label="Detalhes"
          value={detalhes}
          onChange={(e) => setDetalhes(e.target.value)}
          error={erros.details}
          hint="Descrição curta que aparece no catálogo e na proposta."
        />
        <Field
          as="textarea"
          label="Acessórios"
          wrapperClassName="cat-accessories-field"
          value={acessorios}
          onChange={(e) => setAcessorios(e.target.value)}
          error={erros.accessories}
          hint="Um acessório por linha."
          placeholder={'01 cabo de força\n01 mochila de transporte'}
        />
        <div className="field-grid">
          <Field
            label="Valor unitário (R$)"
            required
            className="mono"
            inputMode="decimal"
            value={valor}
            onChange={(e) => setValor(e.target.value)}
            error={erros['price.unitValue']}
            hint="Use vírgula para os centavos, ex.: 24829,20."
          />
          <Field
            label="IPI (%)"
            className="mono"
            inputMode="decimal"
            value={ipi}
            onChange={(e) => setIpi(e.target.value)}
            error={erros['price.ipi']}
          />
        </div>
        <Field
          label="Vigência a partir de"
          type="date"
          value={validade}
          onChange={(e) => setValidade(e.target.value)}
          error={erros['price.validFrom']}
          hint="Pode ser uma data futura — o preço só passa a valer nesse dia."
        />
        <Field
          as="textarea"
          label="Nota"
          value={nota}
          onChange={(e) => setNota(e.target.value)}
          error={erros['price.note']}
          hint="Opcional — ex.: “Preço inicial de cadastro”."
        />
      </form>
    </Modal>
  );
}
